import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import api from "api/api";

// import Mui components
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";

// import mui icons
import CloseIcon from "@mui/icons-material/Close";
import InsightsIcon from "@mui/icons-material/Insights";

// redux
import { useSelector, useDispatch } from "react-redux";
import { loadTargetKpiState, loadCorrespondingKpiState } from "store/features/driverAnalysisSlice";
import { showNotification } from "store/features/notificationSlice";

import DriverAnalysisConsumerFilters from "./DriverAnalysisConsumerFilters";
// Import common dropdown component and algorithm constants
import DriverAnalysisMethodDropdown, { ALGORITHMS } from "./DriverAnalysisMethodDropdown";

const DriverAnalysisFilters = ({ selectedStudy = [], selectedBackendVariableForDriverAnalysis = [], targetKpi = "", targetKpiLabel = "" }) => {
    const dispatch = useDispatch();
    // Driver analysis
    const [driverAnalysisIdentifiers, setDriverAnalysisIdentifiers] = useState([]);
    const [driverAnalysisFiltersData, setDriverAnalysisFiltersData] = useState([]);
    const [studyPeriodOptions, setStudyPeriodOptions] = useState([]);
    const [studyReferenceOptions, setStudyReferenceOptions] = useState([]);
    const [productsOptions, setProductsOptions] = useState([]);
    const [identifierError, setIdentifierError] = useState("");

    const [driverAnalysisFilterModel, setDriverAnalysisFilterModel] = useState(false);

    const [studyPeriodSelected, setStudyPeriodSelected] = useState([]);
    const [studyReferenceSelected, setStudyReferenceSelected] = useState([]);
    const [productsSelected, setProductsSelected] = useState([]);
    const [identifier, setIdentifier] = useState("");

    const [consumerFilter, setConsumerFilter] = useState({});
    const [cfSelectionData, setCfSelectionData] = useState([]);
    const [cfSelectionLabelsData, setCfSelectionLabelsData] = useState([]);

    const [selectedAlgorithm, setSelectedAlgorithm] = useState(null);

    const role = useSelector((state) => state.userStore.role) || null;
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const coreDataId = selectedStudy[0]?.id || null;
    const studyTypeMethod = selectedStudy.length > 0 ? [...new Set(selectedStudy.map((ele) => ele.study_type_method))].join(", ") : null;

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const isBeautyAndWellbeing = isHairCare || isSkinCare;

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const resetDriverAnalysisFilterSelection = () => {
        setStudyPeriodSelected([]);
        setStudyReferenceSelected([]);
        setProductsSelected([]);
        setIdentifier("");
        // Reset algorithm state when filters are reset
        setSelectedAlgorithm(null);
    };

    const handleDriverAnalysisFilterModel = () => {
        setDriverAnalysisFilterModel((prev) => !prev);
        resetDriverAnalysisFilterSelection();
        setCfSelectionData([]);
        setCfSelectionLabelsData([]);
    };

    const fetchDriverAnalysisIdentifiers = useCallback(async () => {
        const url = process.env.REACT_APP_SERVER_PATH + `/driver-analysis-output/identifiers/all/`;
        try {
            api.get(url)
                .then(async (res) => {
                    const data = res?.data || [];
                    const uniqueIdentifiers = [...new Set(data.map((item) => item.identifier?.toLowerCase().trim()))].filter((identifier) => identifier !== "" && identifier !== null && identifier !== undefined);
                    setDriverAnalysisIdentifiers([...uniqueIdentifiers]);
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, []);

    const fetchConsumerFilterData = useCallback(async () => {
        try {
            const products = selectedStudy[0]?.products?.join(",");
            let url =
                process.env.REACT_APP_SERVER_PATH +
                `/data-mappings/dynamic-consumer-filters/?uniq=true&core_data_model=${coreDataId}&variable_category=${encodeURIComponent("Consumer Filter,Study Filter,Consumer Filter related to Product Trial")}&combined=true&products=${encodeURIComponent(products)}`;
            if (productCategoryId) {
                url += `&product_category=${productCategoryId}`;
            }
            if (studyTypeMethod) {
                url += `&study_type_method=${studyTypeMethod}`;
            }
            await api
                .get(url)
                .then((res) => {
                    setConsumerFilter(res?.data || {});
                })
                .catch((error) => {
                    console.error("Error 1:", error.message);
                });
        } catch (error) {
            console.error("Error 2:", error.message);
        }
    }, [coreDataId, studyTypeMethod]);

    useEffect(() => {
        fetchConsumerFilterData();
    }, [fetchConsumerFilterData]);

    useEffect(() => {
        fetchDriverAnalysisIdentifiers();
    }, [fetchDriverAnalysisIdentifiers]);

    useEffect(() => {
        const exists = driverAnalysisIdentifiers.includes(identifier.toLowerCase().trim());
        if (exists) {
            setIdentifierError("Report with given identifier already exist. Please choose a different identifier.");
        } else {
            setIdentifierError("");
        }
    }, [identifier, driverAnalysisIdentifiers]);

    useEffect(() => {
        const _filtersData = selectedStudy.map((ele) => {
            const ulProArr = ele.products?.map((pro) => pro);
            const ulCompProArr = ele.competitor_products?.map((pro) => pro);

            return {
                coreDataId: ele.id.toString(),
                studyRef: ele.study_reference__title,
                products: [...ulProArr, ...ulCompProArr],
                studyPeriod: ele.study_period__title.split("/")[0],
            };
        });
        setDriverAnalysisFiltersData(_filtersData);
        const _studyPeriodsOpt = Array.from(new Set(_filtersData.map((ele) => ele.studyPeriod)));
        setStudyPeriodOptions(_studyPeriodsOpt);
    }, [selectedStudy]);

    // filter study reference based on study period
    useEffect(() => {
        const _data = driverAnalysisFiltersData.filter((ele) => studyPeriodSelected.includes(ele.studyPeriod));
        const _studyRefOpt = _data.map((ele) => {
            return {
                label: ele.studyRef,
                value: ele.coreDataId,
            };
        });
        setStudyReferenceOptions(_studyRefOpt);

        //intersection performed on studyRef option and selected ref
        const _selectedRef = _studyRefOpt.filter((ele) => studyReferenceSelected.some((datum) => datum.value === ele.value && datum.label === ele.label));
        setStudyReferenceSelected(_selectedRef);
    }, [studyPeriodSelected]);

    // filter products based on study ref and study period
    useEffect(() => {
        const _studyReferenceSelected = studyReferenceSelected.map((ele) => ele.value);
        const _data = driverAnalysisFiltersData.filter((ele) => _studyReferenceSelected.includes(ele.coreDataId));
        const _productsOpt = _data
            .map((ele) =>
                [...new Set(ele.products)].map((datum) => {
                    return {
                        label: datum,
                        value: datum,
                        coreDataId: ele.coreDataId,
                    };
                })
            )
            .flat();
        setProductsOptions(_productsOpt);

        //intersection performed on products option and selected products
        const _selectedPro = _productsOpt.filter((ele) => productsSelected.some((datum) => datum.coreDataId === ele.coreDataId && datum.value === ele.value));
        setProductsSelected(_selectedPro);
    }, [studyReferenceSelected]);

    const returnCheckBoxes = (label, value, i, filterName, coreDataId) => {
        const proTitle = `${label}__${coreDataId}`;
        const proTestCode = productsTestCodeMapData[proTitle];
        return (
            <FormControlLabel
                key={`${filterName}-${label.replace(/\s/g, "").toLowerCase()}`}
                sx={{
                    "& .MuiTypography-root": {
                        fontSize: 14,
                        color: "#495057",
                        marginTop: "1px",
                    },
                }}
                id={`${filterName}-${label.replace(/\s/g, "").toLowerCase()}`}
                control={
                    <Checkbox
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 18,
                                // color: "#495057",
                            },
                        }}
                        // color="secondary"
                        id={`${filterName}-${label.replace(/\s/g, "").toLowerCase()}`}
                        checked={
                            filterName === "products"
                                ? productsSelected.some((ele) => ele.value === value && ele.coreDataId === coreDataId)
                                : filterName === "studyPeriod"
                                    ? studyPeriodSelected.includes(value)
                                    : filterName === "studyReference"
                                        ? studyReferenceSelected.some((ele) => ele.value === value)
                                        : false
                        }
                        onChange={(e) => handleOnChange(e, label, i, filterName, coreDataId)}
                        value={value}
                        name={`${filterName}-${label.replace(/\s/g, "").toLowerCase()}`}
                    />
                }
                label={
                    label.includes(proTestCode) ? (
                        <span>
                            {label.split("/")[0]}{" "}
                            {isOralCare && filterName === "products" && (
                                <>
                                    / <span style={{ color: "#6f42c1" }}>{proTestCode}</span>
                                </>
                            )}
                        </span>
                    ) : (
                        <span>
                            {label}{" "}
                            {isOralCare && filterName === "products" && (
                                <>
                                    / <span style={{ color: "#6f42c1" }}>{proTestCode}</span>
                                </>
                            )}
                        </span>
                    )
                }
            />
        );
    };

    const returnSelectAllAndClear = (filterName) => {
        let isChecked = false;
        if (filterName === "studyPeriod") {
            isChecked = studyPeriodSelected.length > 0 && studyPeriodSelected.length === studyPeriodOptions.length;
        } else if (filterName === "studyReference") {
            isChecked = studyReferenceSelected.length > 0 && studyReferenceSelected.length === studyReferenceOptions.length;
        } else if (filterName === "products") {
            isChecked = productsSelected.length > 0 && productsSelected.length === productsOptions.length;
        }
        return (
            <FormControlLabel
                sx={{
                    "& .MuiTypography-root": {
                        fontSize: 14,
                        color: "#495057",
                        marginTop: "1px",
                    },
                }}
                control={
                    <Checkbox
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 18,
                            },
                        }}
                        checked={isChecked}
                        onChange={(e) => handleAllChecked(e, filterName)}
                    />
                }
                label="Select All"
            />
        );
    };

    const handleOnChange = (e, label, i, filterName, coreDataId) => {
        if (filterName === "studyPeriod") {
            if (e.target.checked) {
                setStudyPeriodSelected((checkedItems) => [...checkedItems, e.target.value]);
            } else {
                const filteredId = studyPeriodSelected.filter((ele) => ele !== e.target.value);
                setStudyPeriodSelected(filteredId);
            }
        } else if (filterName === "studyReference") {
            if (e.target.checked) {
                setStudyReferenceSelected((checkedItems) => [
                    ...checkedItems,
                    {
                        label: label,
                        value: e.target.value,
                    },
                ]);
            } else {
                const filteredId = studyReferenceSelected.filter((ele) => ele.value !== e.target.value);
                setStudyReferenceSelected(filteredId);
            }
        } else if (filterName === "products") {
            if (e.target.checked) {
                setProductsSelected((checkedItems) => [
                    ...checkedItems,
                    {
                        label: label,
                        value: e.target.value,
                        coreDataId: coreDataId,
                    },
                ]);
            } else {
                const filteredId = productsSelected.filter((ele) => ele.value !== e.target.value);
                setProductsSelected(filteredId);
            }
        } else {
            console.log("wrong filter", filterName);
        }
    };

    const handleAllChecked = (e, filterName) => {
        if (filterName === "studyPeriod") {
            if (e.target.checked) {
                setStudyPeriodSelected(studyPeriodOptions);
            } else {
                setStudyPeriodSelected([]);
            }
        } else if (filterName === "studyReference") {
            if (e.target.checked) {
                const options = studyReferenceOptions.map((ele) => {
                    return {
                        label: ele.label,
                        value: ele.value,
                    };
                });
                setStudyReferenceSelected(options);
            } else {
                setStudyReferenceSelected([]);
            }
        } else if (filterName === "products") {
            if (e.target.checked) {
                const options = productsOptions.map((ele) => {
                    return {
                        label: ele.label,
                        value: ele.value,
                        coreDataId: ele.coreDataId,
                    };
                });
                setProductsSelected(options);
            } else {
                setProductsSelected([]);
            }
        } else {
            console.log("wrong filter", filterName);
        }
    };

    const returnFiltersPayload = () => {
        let _flt = {};

        const proCodes =
            productsSelected?.map((ele) => {
                return {
                    coreDataId: ele.coreDataId,
                    productCode: ele.value,
                };
            }) || [];
        _flt["product_codes"] = proCodes;

        if (isOralCare || isHairCare || isSkinCare || isUnileverFragranceHouse) {
            let _consumerFilters = {};
            for (const key in cfSelectionData) {
                _consumerFilters[key] = cfSelectionData[key].join(",");
            }
            _flt["consumer_filter"] = _consumerFilters;
        }

        return _flt;
    };

    const returnCoreDataIds = () => {
        let _cdi = [];

        if (studyPeriodSelected.length !== 0) {
            if (productsSelected.length === 0 && productsOptions.length === 0 && studyReferenceSelected.length === 0) {
                _cdi = studyReferenceOptions.map((ele) => ele.value).join(",");
            } else if (productsSelected.length === 0 && productsOptions.length !== 0 && studyReferenceSelected.length !== 0) {
                _cdi = studyReferenceSelected.map((ele) => ele.value).join(",");
            } else if (productsSelected.length !== 0) {
                _cdi = Array.from(new Set(productsSelected.map((ele) => ele.coreDataId))).join(",");
            } else {
                _cdi = selectedStudy.map((ele) => ele.id).join(",");
            }
        } else {
            _cdi = selectedStudy.map((ele) => ele.id).join(",");
        }
        return _cdi;
    };

    const returnInfo = () => {
        // info
        let infoStPer = [];
        let infoStRef = [];
        let infoPro = [];

        // info only of view
        if (productsSelected.length !== 0) {
            // SP SR PR
            const _cdId = Array.from(new Set(productsSelected.map((ele) => ele.coreDataId).map(Number)));
            const filteredStudy = selectedStudy.filter((ele) => _cdId.includes(ele.id));

            infoPro =
                productsSelected?.map((ele) => {
                    return {
                        coreDataId: ele.coreDataId,
                        productCode: ele.value,
                    };
                }) || [];

            infoStPer = Array.from(new Set(filteredStudy.map((ele) => ele.study_period__title.split("/")[0])));
            infoStRef = Array.from(new Set(filteredStudy.map((ele) => ele.study_reference__title)));
        } else if (productsSelected.length === 0 && studyReferenceSelected.length !== 0) {
            // SP SR
            const _cdId = Array.from(new Set(studyReferenceSelected.map((ele) => ele.value).map(Number)));
            const filteredStudy = selectedStudy.filter((ele) => _cdId.includes(ele.id));

            infoStPer = Array.from(new Set(filteredStudy.map((ele) => ele.study_period__title.split("/")[0])));
            infoStRef = Array.from(new Set(filteredStudy.map((ele) => ele.study_reference__title)));

            infoPro = filteredStudy
                .map((datum) => {
                    const pro = datum.products.map((ele) => {
                        return {
                            coreDataId: datum.id,
                            productCode: ele,
                        };
                    });
                    const ulPro = datum.competitor_products.map((ele) => {
                        return {
                            coreDataId: datum.id,
                            productCode: ele,
                        };
                    });
                    return [...pro, ...ulPro];
                })
                .flat();
        } else if (productsSelected.length === 0 && studyReferenceSelected.length === 0 && studyPeriodSelected.length !== 0) {
            // SP
            const filteredStudy = selectedStudy.filter((ele) => studyPeriodSelected.includes(ele.study_period__title.split("/")[0]));

            infoStPer = Array.from(new Set(filteredStudy.map((ele) => ele.study_period__title.split("/")[0])));
            infoStRef = Array.from(new Set(filteredStudy.map((ele) => ele.study_reference__title)));
            infoPro = filteredStudy
                .map((datum) => {
                    const pro = datum.products.map((ele) => {
                        return {
                            coreDataId: datum.id,
                            productCode: ele,
                        };
                    });
                    const ulPro = datum.competitor_products.map((ele) => {
                        return {
                            coreDataId: datum.id,
                            productCode: ele,
                        };
                    });
                    return [...pro, ...ulPro];
                })
                .flat();
        } else {
            infoStPer = Array.from(new Set(selectedStudy.map((ele) => ele.study_period__title.split("/")[0])));
            infoStRef = Array.from(new Set(selectedStudy.map((ele) => ele.study_reference__title)));
            infoPro = selectedStudy
                .map((datum) => {
                    const pro = datum.products.map((ele) => {
                        return {
                            coreDataId: datum.id,
                            productCode: ele,
                        };
                    });
                    const ulPro = datum.competitor_products.map((ele) => {
                        return {
                            coreDataId: datum.id,
                            productCode: ele,
                        };
                    });
                    return [...pro, ...ulPro];
                })
                .flat();
        }

        const _info = {
            studyReference: infoStRef,
            studyPeriod: infoStPer,
            studyProduct: infoPro,
        };

        return _info;
    };

    // Handle algorithm dropdown change (only for Hair Care & Skin Care)
    const handleAlgorithmChange = (selectedOption) => {
        setSelectedAlgorithm(selectedOption);
    };

    const handleDriverAnalysis = () => {
        // Check if identifier is empty before making the API call
        if (!identifier) {
            setIdentifierError("Identifier is a mandatory field. Please provide it to create the driver analysis report."); // Set error message
            return; // Prevent API call
        } else {
            setIdentifierError(""); // Clear error message if identifier is valid
        }

        dispatch(loadCorrespondingKpiState(selectedBackendVariableForDriverAnalysis));
        dispatch(loadTargetKpiState(targetKpi));

        async function postDriverAnalysisMetadata() {
            try {
                const _cdi = returnCoreDataIds();
                const _flt = returnFiltersPayload();
                const _info = returnInfo();

                const url = process.env.REACT_APP_SERVER_PATH + `/driver-analysis-output/`;

                const payload = {
                    product_category: productCategoryId,
                    core_data_ids: _cdi,
                    identifier: identifier,
                    title: identifier,
                    target_kpi: targetKpi,
                    target_kpi_label: targetKpiLabel,
                    corresponding_kpi: selectedBackendVariableForDriverAnalysis.join(","),
                    filters: JSON.stringify(_flt),
                    info: JSON.stringify(_info),
                    algorithm: (isBeautyAndWellbeing || isUnileverFragranceHouse) && selectedAlgorithm ? selectedAlgorithm.value : ALGORITHMS.RANDOM_FOREST_CLASSIFIER.value,
                };

                api.post(url, payload)
                    .then()
                    .catch(async (error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        }
        if (selectedStudy && selectedBackendVariableForDriverAnalysis && selectedStudy.length !== 0 && selectedBackendVariableForDriverAnalysis.length !== 0 && targetKpi && targetKpi !== "" && targetKpi !== null) {
            if (productCategoryId) {
                postDriverAnalysisMetadata();
                handleDriverAnalysisFilterModel();
                handleError("success", `Your report ${identifier} will be generated shortly. It will be available within the 'Unsaved' view. You can continue using the tool.`);
            } else {
                handleError("error", "Product category is not selected");
            }
        } else {
            handleError("error", "Target Kpi or Corresponding Kpis is empty.");
        }
    };

    const handleFiltersData = (labelsData, data) => {
        setCfSelectionData(data);
        setCfSelectionLabelsData(labelsData);
    };

    return (
        <>
            <Button
                size="small"
                // className="custom-btn custom-btn-outlined color-blue me-4"
                className={(!(isBeautyAndWellbeing || isUnileverFragranceHouse) && (role && role.toLowerCase() != "admin")) || selectedBackendVariableForDriverAnalysis.length < 5 || selectedBackendVariableForDriverAnalysis.includes("R_OO") ? "custom-btn bold-content" : "custom-btn custom-btn-outlined color-blue bold-content"}
                style={{ marginRight: "1rem" }}
                variant="outlined"
                disabled={(!(isBeautyAndWellbeing || isUnileverFragranceHouse) && (role && role.toLowerCase() != "admin")) || selectedBackendVariableForDriverAnalysis.length < 5 || selectedBackendVariableForDriverAnalysis.includes("R_OO")}
                startIcon={<InsightsIcon />}
                onClick={handleDriverAnalysisFilterModel}
            // onClick={handleDriverAnalysis}
            >
                Driver Analysis
            </Button>
            {driverAnalysisFilterModel && (
                <Dialog
                    open={driverAnalysisFilterModel}
                    fullWidth={true}
                    maxWidth="md"
                    sx={{ "& .MuiDialog-paper": { width: "100%", minHeight: 750, maxHeight: 750 } }}
                >
                    <DialogTitle
                        style={{
                            backgroundColor: "rgb(51, 94, 234,0.1)",
                            color: "rgb(51, 94, 234)",
                            fontSize: 15,
                            textAlign: "center",
                            padding: "10px 12px",
                        }}
                        // className="liner-light-blue-background-2"
                        id="alert-dialog-title"
                    >
                        Driver Analysis Filters
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 2,
                                color: "rgb(51, 94, 234) !important",
                            }}
                            onClick={handleDriverAnalysisFilterModel}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        {/* Driver Analysis Method Dropdown - Using common component */}
                        <DriverAnalysisMethodDropdown
                            selectedAlgorithm={selectedAlgorithm}
                            onAlgorithmChange={handleAlgorithmChange}
                            margin="15px 0 20px 0"
                            width="50%"
                        />

                        <div style={{ marginTop: (isBeautyAndWellbeing || isUnileverFragranceHouse) ? "0px" : "20px" }}>
                            <div className="row">
                                <div className="col-3">
                                    <div
                                        style={{
                                            // boxShadow: "rgba(0, 0, 0, 0.24) 0px 0px 4px",
                                            border: "1px solid rgba(0, 0, 0, 0.24)",
                                            borderRadius: "5px",
                                            padding: "10px 0px 20px",
                                            height: "150px",
                                            overflow: "auto",
                                        }}
                                    >
                                        <p
                                            style={{
                                                textAlign: "center",
                                                marginBottom: "0px",
                                            }}
                                        >
                                            Study Period
                                        </p>
                                        <hr
                                            style={{
                                                margin: "10px 0px 0px 0px",
                                            }}
                                        />
                                        <div
                                            className="px-3"
                                            style={{
                                                overflowY: "auto",
                                            }}
                                        >
                                            <FormGroup>
                                                {isOralCare && studyPeriodOptions.length !== 0 && returnSelectAllAndClear("studyPeriod")}
                                                {studyPeriodOptions.length !== 0 &&
                                                    studyPeriodOptions?.map((ele, i) => {
                                                        return returnCheckBoxes(ele, ele, i, "studyPeriod");
                                                    })}
                                            </FormGroup>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div
                                        style={{
                                            // boxShadow: "rgba(0, 0, 0, 0.24) 0px 0px 4px",
                                            border: "1px solid rgba(0, 0, 0, 0.24)",
                                            borderRadius: "5px",
                                            padding: "10px 0px 20px",
                                            height: "150px",
                                            overflow: "auto",
                                        }}
                                    >
                                        <p
                                            style={{
                                                textAlign: "center",
                                                marginBottom: "0px",
                                            }}
                                        >
                                            Study Reference
                                        </p>
                                        <hr
                                            style={{
                                                margin: "10px 0px 0px 0px",
                                            }}
                                        />
                                        <div
                                            className="px-3"
                                            style={{
                                                overflowY: "scroll",
                                            }}
                                        >
                                            <FormGroup>
                                                {isOralCare && studyPeriodSelected.length !== 0 && returnSelectAllAndClear("studyReference")}
                                                {studyReferenceOptions !== 0 &&
                                                    studyReferenceOptions?.map((ele, i) => {
                                                        return returnCheckBoxes(ele.label, ele.value, i, "studyReference", ele.coreDataId);
                                                    })}
                                            </FormGroup>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-5">
                                    <div
                                        style={{
                                            // boxShadow: "rgba(0, 0, 0, 0.24) 0px 0px 0px",
                                            border: "1px solid rgba(0, 0, 0, 0.24)",
                                            borderRadius: "5px",
                                            padding: "10px 0px 20px",
                                            height: "150px",
                                            overflow: "auto",
                                        }}
                                    >
                                        <p
                                            style={{
                                                textAlign: "center",
                                                marginBottom: "0px",
                                            }}
                                        >
                                            Products
                                        </p>
                                        <hr
                                            style={{
                                                margin: "10px 0px 0px 0px",
                                            }}
                                        />

                                        <div
                                            className="px-3"
                                            style={{
                                                overflowY: "scroll",
                                            }}
                                        >
                                            <FormGroup>
                                                {isOralCare && studyReferenceSelected.length !== 0 && returnSelectAllAndClear("products")}
                                                {productsOptions !== 0 &&
                                                    productsOptions?.map((ele, i) => {
                                                        return returnCheckBoxes(ele.label, ele.value, i, "products", ele.coreDataId);
                                                    })}
                                            </FormGroup>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {(isOralCare || isHairCare || isSkinCare || isUnileverFragranceHouse) && (
                                <div>
                                    <div
                                        style={{
                                            border: "1px solid rgba(0, 0, 0, 0.24)",
                                            borderRadius: "5px",
                                            padding: "10px",
                                            fontSize: 12,
                                            display: "block",
                                            width: "100%",
                                            height: "240px",
                                            marginTop: "12px",
                                        }}
                                    >
                                        Consumer Filter
                                        <DriverAnalysisConsumerFilters
                                            handleFiltersData={handleFiltersData}
                                            consumerFilter={consumerFilter}
                                        />
                                        {cfSelectionLabelsData.map((datum) => {
                                            const mainFilter = datum[0];
                                            const subFilter = datum[1]?.join(",");
                                            return (
                                                <div
                                                    key={mainFilter}
                                                    style={{
                                                        display: "flex",
                                                        margin: "10px",
                                                    }}
                                                >
                                                    <p style={{ margin: "0px" }}>{mainFilter} :&nbsp;</p>
                                                    <p style={{ margin: "0px" }}>{`[${subFilter}]`}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions
                        style={{
                            margin: "0px 16px",
                            flexFlow: "column",
                        }}
                    >
                        <div
                            style={{
                                border: "1px solid rgba(0, 0, 0, 0.24)",
                                borderRadius: "5px",
                                padding: "10px",
                                fontSize: 12,
                                display: "block",
                                width: "100%",
                                marginBottom: 10,
                            }}
                        >
                            Provide the identifier to the driver analysis report:
                            <input
                                type="text"
                                className="search-bar-input text-input"
                                placeholder="Identifier"
                                value={identifier}
                                onChange={(e) => {
                                    setIdentifier(e.target.value);
                                    setIdentifierError(""); // Clear error message on input change
                                }}
                                style={{
                                    minWidth: "350px",
                                    fontSize: "14px",
                                    padding: "4.5px 10px",
                                    // marginLeft: "10px",
                                    borderRadius: "5px",
                                }}
                            />
                            {identifierError && <div style={{ color: "red", marginTop: "5px" }}>{identifierError}</div>}
                        </div>
                        <div
                            style={{
                                border: "1px solid rgba(0, 0, 0, 0.24)",
                                borderRadius: "5px",
                                padding: "10px",
                                fontSize: 12,
                                display: "block",
                                width: "100%",
                                marginBottom: 10,
                            }}
                        >
                            Note:
                            <ul>
                                <li>No filters selected means all selection.</li>
                                <li>Only Study Period selected means all the Study Reference of the selected Study Period.</li>
                                <li>Only Study period and Study Reference selected means all the products of the selected Study Period and Study Reference.</li>
                            </ul>
                        </div>
                        {/* <Button
                                    size="small"
                                    className={"custom-btn custom-btn-outlined color-blue"}
                                    variant="outlined"
                                    onClick={resetDriverAnalysisFilterSelection}
                                    startIcon={<RestartAltIcon />}
                                >
                                    Reset
                                </Button> */}
                        <Button
                            size="small"
                            className={"custom-btn custom-btn-outlined color-blue"}
                            variant="outlined"
                            style={{
                                margin: 10,
                            }}
                            // disabled={disableResultButton}
                            onClick={handleDriverAnalysis}
                            startIcon={<InsightsIcon />}
                        >
                            Generate Driver Analysis Report
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

// Typechecking props for the DriverAnalysisFilters
DriverAnalysisFilters.propTypes = {
    selectedStudy: PropTypes.array,
    selectedBackendVariableForDriverAnalysis: PropTypes.array,
    targetKpi: PropTypes.string,
    targetKpiLabel: PropTypes.string,
};

export default DriverAnalysisFilters;
